import { handleActions, combineActions, Action } from 'redux-actions';
import without from 'lodash/without';
import { CollectionStoreSlice } from './types';
import {
  commitCollections,
  commitCollectionsSuccess,
  commitCollectionsFail,
  initializeDeviationCollectionsCustom,
  createCollection,
  createCollectionSuccess,
  createCollectionFail,
  undoChanges,
  toggleCollection,
  changeFave,
  changeFaveSuccess,
  changeFaveFail,
  setFaveState,
  resetJustFaved,
  setPrivatelyCollectedState,
} from './actionCreators';
import { reduxKey } from './constants';
import { PapiDeviation } from '@wix/da-papi-types';
import { unloadDuperbrowseItem } from '../../Duperbrowse/redux/actionCreators';

// helper method to set per-deviation states easier
function setDeviationState<T extends { deviationId: number } | { deviation: PapiDeviation }>(
  state: CollectionStoreSlice,
  { payload }: Action<T>,
  getValues: (
    oldValues: CollectionStoreSlice['deviations'][0] | undefined,
    payload: T
  ) => CollectionStoreSlice['deviations'][0] | undefined
) {
  const deviationId =
    (payload as { deviationId: number })?.deviationId ??
    (payload as { deviation: PapiDeviation })?.deviation?.deviationId;

  // if the action does not have deviation or deviationId, bail
  if (!deviationId) {
    return state;
  }

  // we use a callback to figure out the next state
  const newValues = getValues(state.deviations[deviationId], payload);
  // if callback returns nothing, means there's nothing to change
  if (!newValues) {
    return state;
  }

  return {
    ...state,
    deviations: {
      ...state.deviations,
      [deviationId]: newValues,
    },
  };
}

const handler = handleActions<CollectionStoreSlice, any>(
  {
    [combineActions(undoChanges, unloadDuperbrowseItem).toString()]: (
      state,
      action: ReturnType<typeof undoChanges>
    ) =>
      setDeviationState(
        state,
        action,
        oldValue =>
          oldValue && {
            ...oldValue,
            selectedCollections: oldValue.savedCollections,
          }
      ),
    [initializeDeviationCollectionsCustom.toString()]: (
      state,
      action: ReturnType<typeof initializeDeviationCollectionsCustom>
    ) => {
      if (!action.payload) {
        return state;
      }
      const { ignorePreselectedCollections, isPrivateCollections } = action.payload;
      return {
        ...setDeviationState(state, action, (oldValue, payload) => ({
          isInitializedForCollecting: !isPrivateCollections
            ? true
            : !!oldValue?.isInitializedForCollecting,
          isInitializedForPrivateCollecting: isPrivateCollections
            ? true
            : !!oldValue?.isInitializedForPrivateCollecting,
          isFavourite: !isPrivateCollections && payload.collectionids.length > 0,
          isPrivatelyCollected: isPrivateCollections && payload.collectionids.length > 0,
          isLoading: false,
          faveCount: payload.faveCount,
          selectedCollections:
            ignorePreselectedCollections || isPrivateCollections ? [] : payload.collectionids,
          selectedPrivateCollections:
            ignorePreselectedCollections || !isPrivateCollections ? [] : payload.collectionids,
          savedCollections: payload.collectionids,
          justFaved: false,
        })),
        isNewCollectionSaving: false,
      };
    },
    [toggleCollection.toString()]: (state, action: ReturnType<typeof toggleCollection>) =>
      setDeviationState(state, action, (oldValue, payload) => {
        if (!oldValue) {
          return;
        }

        const selectedCollectionsKey = payload.isPrivateCollections
          ? 'selectedPrivateCollections'
          : 'selectedCollections';

        const selected = oldValue[selectedCollectionsKey];
        // if the folder was selected, we remove it; otherwise add it
        const newSelected = selected.includes(payload.folderId)
          ? without(selected, payload.folderId)
          : [...selected, payload.folderId];

        return {
          ...oldValue,
          [selectedCollectionsKey]: newSelected,
        };
      }),
    [commitCollections.toString()]: (state, action: ReturnType<typeof commitCollections>) =>
      setDeviationState(
        state,
        action,
        oldValue =>
          oldValue && {
            ...oldValue,
            isLoading: true,
          }
      ),
    [commitCollectionsSuccess.toString()]: (
      state,
      action: ReturnType<typeof commitCollectionsSuccess>
    ) => {
      return setDeviationState(
        state,
        action,
        (oldValue, { folderIds }) =>
          oldValue && {
            ...oldValue,
            selectedCollections: folderIds,
            savedCollections: folderIds,
            isLoading: false,
          }
      );
    },
    [commitCollectionsFail.toString()]: (state, action: ReturnType<typeof commitCollectionsFail>) =>
      setDeviationState(
        state,
        action,
        oldValue =>
          oldValue && {
            ...oldValue,
            isLoading: false,
          }
      ),
    [createCollection.toString()]: state => ({
      ...state,
      isNewCollectionSaving: true,
    }),
    [createCollectionSuccess.toString()]: state => ({
      ...state,
      isNewCollectionSaving: false,
    }),
    [createCollectionFail.toString()]: state => ({
      ...state,
      isNewCollectionSaving: false,
    }),
    [setPrivatelyCollectedState.toString()]: (
      state,
      action: ReturnType<typeof setPrivatelyCollectedState>
    ) =>
      setDeviationState(
        state,
        action,
        oldValue =>
          oldValue && {
            ...oldValue,
            isPrivatelyCollected: action.payload.to,
          }
      ),
    [combineActions(changeFave, setFaveState).toString()]: (
      state,
      action: ReturnType<typeof changeFave | typeof setFaveState>
    ) =>
      setDeviationState(state, action, (oldValue, payload) => {
        const {
          deviation,
          deviation: { isFavourited },
          to,
        } = payload;

        // sometimes we only want to change the favedness in the store
        // eg. when the collections are being updated
        // this means we can skip changing a bunch of values
        const isPureSet = action.type === setFaveState.toString();
        if (isPureSet && !oldValue) {
          return;
        }

        const isFavouritedNow =
          oldValue && oldValue.isFavourite !== undefined
            ? oldValue.isFavourite
            : isFavourited || false;

        const willBeFavourited = to === undefined ? !isFavouritedNow : to;

        if (isFavouritedNow === willBeFavourited) {
          // nothing to do
          return oldValue;
        }

        const favesNow =
          oldValue && oldValue.faveCount !== undefined
            ? oldValue.faveCount
            : (deviation.stats && deviation.stats.favourites) || 0;

        return {
          ...oldValue,
          isFavourite: willBeFavourited,
          justFaved: !isPureSet && !isFavouritedNow && willBeFavourited,
          faveCount: Math.max(favesNow + (willBeFavourited ? 1 : -1), 0),
          isLoading: !isPureSet,
          isInitializedForCollecting: isPureSet ? oldValue!.isInitializedForCollecting : false,
          isInitializedForPrivateCollecting: isPureSet
            ? oldValue!.isInitializedForPrivateCollecting
            : false,
          savedCollections: isPureSet ? oldValue!.savedCollections : [],
          selectedCollections: isPureSet ? oldValue!.selectedCollections : [],
          selectedPrivateCollections: isPureSet ? oldValue!.selectedPrivateCollections : [],
        };
      }),
    [changeFaveSuccess.toString()]: (state, action: ReturnType<typeof changeFaveSuccess>) =>
      setDeviationState(
        state,
        action,
        oldValue =>
          oldValue && {
            ...oldValue,
            isLoading: false,
          }
      ),
    [changeFaveFail.toString()]: (state, action: ReturnType<typeof changeFaveFail>) =>
      setDeviationState(
        state,
        action,
        oldValue =>
          oldValue && {
            ...oldValue,
            isFavourite: !oldValue.isFavourite,
            justFaved: false,
            faveCount: oldValue.faveCount + (oldValue.isFavourite ? -1 : 1),
          }
      ),
    [resetJustFaved.toString()]: (state, action: ReturnType<typeof resetJustFaved>) =>
      setDeviationState(
        state,
        action,
        oldValue =>
          oldValue && {
            ...oldValue,
            justFaved: false,
          }
      ),
  },
  {
    deviations: {},
    isNewCollectionSaving: false,
  }
);

export default {
  [reduxKey as string]: handler,
};
